<template>
  <div class="competency-manager">
    <div class="header-section">
      <h1>Competency Group Manager</h1>
      <p class="subtitle">
        Manage and organize your cybersecurity competencies
      </p>
    </div>

    <div class="action-bar">
      <button @click="addNewCompetencyGroup" class="add-button">
        <span class="icon">+</span>
        Add New Competency Group
      </button>
      <button
        @click="saveChanges"
        :disabled="isSubmitDisabled"
        class="submit-button"
      >
        <span class="icon">💾</span>
        {{ submitButtonText }}
      </button>
    </div>

    <!-- Save Message -->
    <div
      v-if="showSaveMessage"
      class="save-message"
      :class="{ error: saveMessage.includes('Error') }"
    >
      {{ saveMessage }}
    </div>

    <!-- Authentication Notice -->
    <div v-if="!isAuthenticated" class="auth-notice">
      <p>⚠️ You must be logged in to save your competency changes.</p>
      <p>
        Please <a href="/auth" class="auth-link">login here</a> to continue.
      </p>
    </div>

    <div class="accordion">
      <div
        v-for="(group, index) in competencyGroups"
        :key="index"
        class="accordion-item"
      >
        <div class="accordion-header" @click="toggleAccordion(index)">
          <div class="header-content">
            <h3>{{ group.competencyGrouping || "New Competency Group" }}</h3>
            <span class="competency-count"
              >{{ group.competencies.length }} competencies</span
            >
          </div>
          <span class="toggle-icon">{{
            expandedSections[index] ? "▼" : "▶"
          }}</span>
        </div>

        <div v-if="expandedSections[index]" class="accordion-content">
          <div class="content-wrapper">
            <!-- Competency Group Details -->
            <div class="form-section">
              <h4>Group Details</h4>
              <div class="form-group">
                <label>Group Title</label>
                <input
                  v-model="group.competencyGrouping"
                  placeholder="Enter group title"
                />
              </div>

              <div class="form-group">
                <label>Description</label>
                <textarea
                  v-model="group.competencyGroupingDescription"
                  placeholder="Enter group description"
                  rows="3"
                ></textarea>
              </div>
            </div>

            <!-- Competencies List -->
            <div class="competencies-section">
              <div class="section-header">
                <h4>Competencies ({{ group.competencies.length }})</h4>
                <button
                  @click="addNewCompetency(index)"
                  class="add-competency-button"
                >
                  <span class="icon">+</span>
                </button>
              </div>

              <div
                v-for="(competency, compIndex) in group.competencies"
                :key="compIndex"
                class="competency-item"
              >
                <div class="competency-header">
                  <h5>{{ competency.competencyTitle || "New Competency" }}</h5>
                  <button
                    @click="removeCompetency(index, compIndex)"
                    class="remove-button"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>

                <div class="form-group">
                  <label>Title</label>
                  <input
                    v-model="competency.competencyTitle"
                    placeholder="Enter competency title"
                  />
                </div>

                <div class="form-group">
                  <label>Description</label>
                  <textarea
                    v-model="competency.competencyTitleDescription"
                    placeholder="Enter competency description"
                    rows="3"
                  ></textarea>
                </div>

                <div class="form-group">
                  <label>Type</label>
                  <select v-model="competency.type" class="type-select">
                    <option value="" disabled>Select type</option>
                    <option value="Technical">Technical</option>
                    <option value="Non Technical">Non Technical</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Delete Group Button -->
            <div class="footer-actions">
              <button
                @click="removeCompetencyGroup(index)"
                class="delete-button"
              >
                <i class="fas fa-trash"></i>
                Delete Group
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from "vue";
import { competencyGroups } from "../stores/constants";
import { CompetencyService } from "../services/competencyService";
import { AuthService } from "../services/authService";

export default defineComponent({
  name: "CompetencyManager",
  setup() {
    const expandedSections = ref<boolean[]>([]);
    const localCompetencyGroups = ref([...competencyGroups]);
    const isAuthenticated = ref(false);
    const currentUser = ref<any>(null);
    const isSaving = ref(false);
    const saveMessage = ref("");
    const showSaveMessage = ref(false);

    // Check authentication status
    const checkAuth = () => {
      const user = AuthService.getCurrentUser();
      isAuthenticated.value = !!user;
      currentUser.value = user;
    };

    // Computed property for submit button text
    const submitButtonText = computed(() => {
      if (!isAuthenticated.value) return "Login to Save Changes";
      if (isSaving.value) return "Saving...";
      return "Save Changes";
    });

    // Computed property for submit button disabled state
    const isSubmitDisabled = computed(() => {
      return !isAuthenticated.value || isSaving.value;
    });

    onMounted(() => {
      console.log("localCompetencyGroups:", localCompetencyGroups.value);

      checkAuth();
      expandedSections.value = new Array(
        localCompetencyGroups.value.length
      ).fill(false);

      // Listen for auth state changes
      AuthService.onAuthStateChanged((user: any) => {
        isAuthenticated.value = !!user;
        currentUser.value = user;
      });
    });

    const toggleAccordion = (index: number) => {
      expandedSections.value[index] = !expandedSections.value[index];
    };

    const addNewCompetencyGroup = () => {
      localCompetencyGroups.value.push({
        competencyGrouping: "",
        competencyGroupingDescription: "",
        competencies: [],
      });
      expandedSections.value.push(true);
    };

    const removeCompetencyGroup = (index: number) => {
      localCompetencyGroups.value.splice(index, 1);
      expandedSections.value.splice(index, 1);
    };

    const addNewCompetency = (groupIndex: number) => {
      localCompetencyGroups.value[groupIndex].competencies.push({
        competencyTitle: "",
        competencyTitleDescription: "",
        type: "Technical",
      });
    };

    const removeCompetency = (groupIndex: number, competencyIndex: number) => {
      localCompetencyGroups.value[groupIndex].competencies.splice(
        competencyIndex,
        1
      );
    };

    const saveChanges = async () => {
      if (!isAuthenticated.value) {
        // Redirect to login
        window.location.href = "/auth";
        return;
      }

      isSaving.value = true;
      showSaveMessage.value = false;

      try {
        const result = await CompetencyService.saveCompetencyGroups(
          localCompetencyGroups.value
        );

        saveMessage.value = result.message;
        showSaveMessage.value = true;

        // Hide message after 3 seconds
        setTimeout(() => {
          showSaveMessage.value = false;
        }, 3000);
      } catch (error) {
        saveMessage.value = "Error saving changes. Please try again.";
        showSaveMessage.value = true;
      } finally {
        isSaving.value = false;
      }
    };

    return {
      competencyGroups: localCompetencyGroups,
      expandedSections,
      isAuthenticated,
      currentUser,
      isSaving,
      saveMessage,
      showSaveMessage,
      submitButtonText,
      isSubmitDisabled,
      toggleAccordion,
      addNewCompetencyGroup,
      removeCompetencyGroup,
      addNewCompetency,
      removeCompetency,
      saveChanges,
    };
  },
});
</script>

<style scoped>
.competency-manager {
  max-width: 1200px;
  margin: 1rem auto;
  padding: 2rem;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
  min-height: 100vh;
  position: relative;
  background: #f8f9fa;
  overflow: scroll;
}

.header-section {
  text-align: center;
  margin-bottom: 2rem;
}

.header-section h1 {
  color: #2c3e50;
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

.subtitle {
  color: #7f8c8d;
  font-size: 1.1rem;
}

/* Button Base Styles */
.add-button,
.add-competency-button,
.remove-button,
.delete-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  border: none;
  outline: none;
}

.add-competency-button {
  width: 50px;
}

/* Add Button Styles */
.add-button {
  background-color: #3498db;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  min-width: 200px;
}

.add-button:hover {
  background-color: #2980b9;
  transform: translateY(-1px);
}

/* Add Competency Button Styles */
.add-competency-button {
  background-color: #2ecc71;
  color: white;
  padding: 0.625rem 1.25rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.add-competency-button:hover {
  background-color: #27ae60;
  transform: translateY(-1px);
}

/* Remove Button Styles */
.remove-button {
  background-color: #e74c3c;
  color: white;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 0.9rem;
  padding: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-button:hover {
  background-color: #c0392b;
  transform: scale(1.1);
}

/* Delete Button Styles */
.delete-button {
  background-color: #e74c3c;
  color: white;
  padding: 0.625rem 1.25rem;
  border-radius: 4px;
  font-size: 0.9rem;
  min-width: 120px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.delete-button:hover {
  background-color: #c0392b;
  transform: translateY(-1px);
}

/* Icon Styles */
.icon {
  font-size: 1.1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fas {
  font-size: inherit;
  line-height: 1;
}

/* Button Container Styles */
.action-bar {
  margin-bottom: 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1rem;
  gap: 1rem;
}

/* Submit Button Styles */
.submit-button {
  background-color: #27ae60;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 1rem;
  min-width: 180px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.submit-button:hover:not(:disabled) {
  background-color: #229954;
  transform: translateY(-1px);
}

.submit-button:disabled {
  background-color: #95a5a6;
  cursor: not-allowed;
  transform: none;
}

/* Save Message Styles */
.save-message {
  margin: 1rem;
  padding: 1rem;
  border-radius: 6px;
  text-align: center;
  font-weight: 500;
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.save-message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

/* Authentication Notice Styles */
.auth-notice {
  margin: 1rem;
  padding: 1rem;
  border-radius: 6px;
  text-align: center;
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.auth-notice p {
  margin: 0.5rem 0;
}

.auth-link {
  color: #3498db;
  text-decoration: none;
  font-weight: 500;
}

.auth-link:hover {
  text-decoration: underline;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding: 0 0.5rem;
}

.competency-item {
  background-color: #f8f9fa;
  padding: 1.25rem;
  margin-bottom: 1rem;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  transition: all 0.2s ease;
}

.competency-item:nth-child(odd) {
  background-color: #f1f3f5;
}

.competency-item:hover {
  background-color: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.competency-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0 0.5rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 0.75rem;
}

.competency-header h5 {
  color: #2c3e50;
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
}

.footer-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e0e0e0;
  padding: 1.5rem 0.5rem 0;
}

.accordion {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.accordion-item {
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
}

.accordion-item:last-child {
  border-bottom: none;
}

.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.accordion-header:hover {
  background-color: #e9ecef;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.header-content h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.1rem;
}

.competency-count {
  color: #6c757d;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  text-align: left;
}

.toggle-icon {
  color: #6c757d;
  font-size: 0.9rem;
  margin-left: 1rem;
}

.accordion-content {
  background-color: white;
}

.content-wrapper {
  padding: 1.5rem;
  position: relative;
}

.form-section,
.competencies-section {
  margin-bottom: 2rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  color: #34495e;
  font-weight: 500;
  font-size: 0.9rem;
  text-align: left;
}

.form-group input,
.form-group textarea,
.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.type-select {
  background-color: white;
  cursor: pointer;
}

/* Mobile Responsive Adjustments */
@media (max-width: 768px) {
  .competency-manager {
    padding: 1rem;
  }

  .header-section h1 {
    font-size: 2rem;
  }

  .action-bar {
    flex-direction: column;
    justify-content: center;
    padding: 0;
    gap: 1rem;
  }

  .add-button,
  .submit-button {
    width: 100%;
    max-width: 300px;
  }

  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: flex-start;
    padding: 0;
  }

  .add-competency-button {
    width: 100%;
  }

  .competency-header {
    padding: 0;
  }

  .footer-actions {
    padding: 1.5rem 0 0;
  }
}
</style>
